import { useEffect, useState } from 'react';
import { breakpoints } from '../theme/tokens';

/**
 * Subscribe to a CSS media query. SSR/no-matchMedia safe (returns `false` until the
 * client mounts). Drives the shell's desktop-rail ↔ mobile-bottom-nav switch so only
 * one navigation surface is in the DOM at a time (no duplicate landmarks/links).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** True below the `md` breakpoint (mobile/tablet) — the compact navigation regime. */
export function useIsCompact(): boolean {
  return useMediaQuery(`(max-width: ${compactMaxWidth()})`);
}

function compactMaxWidth(): string {
  // one hair below the md breakpoint so [md, ∞) is the desktop regime
  const md = breakpoints.md; // e.g. '48rem'
  const rem = parseFloat(md);
  return `${rem - 0.01}rem`;
}
